import * as React from 'react';
import { useCameraList } from '@cguard/api-client/hooks/cameras';
import { useCreateCameraPlayback } from '@cguard/api-client/hooks/video-console';
import { recordingCoverage, stopCameraPlayback } from '@cguard/api-client/hooks/evidence-exports';
import type { CameraId } from '@cguard/types';
import { useTranslation } from 'react-i18next';
import { withoutSeededDemoCameras } from '~/lib/production-roster';
import { useSiteNames } from '~/lib/site-names';
import { readActionError } from '~/lib/action-error';

const MAX_CAMERAS = 9;
export function MultiPlayback({ from, to }: { from: string; to: string }): React.JSX.Element {
  const { t } = useTranslation('video'); const sites = useSiteNames();
  const cameras = withoutSeededDemoCameras(useCameraList().data ?? []);
  const [site, setSite] = React.useState('');
  const [chosen, setChosen] = React.useState<CameraId[]>([]);
  const [sessions, setSessions] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [starting, setStarting] = React.useState(false);
  const create = useCreateCameraPlayback();
  const active = React.useRef<string[]>([]);
  const generation = React.useRef(0);
  const grid = React.useRef<HTMLDivElement>(null);
  const stop = React.useCallback(() => {
    generation.current++; for (const url of active.current) void stopCameraPlayback(url).catch(() => undefined);
    active.current = []; setSessions({}); setStarting(false);
  }, []);
  React.useEffect(() => { stop(); return stop; }, [from, to, chosen, stop]);
  async function start(): Promise<void> {
    stop(); const run = generation.current; setStarting(true); setErrors({});
    // Cap simultaneous session creation at three; every late reply is explicitly released.
    for (let index = 0; index < chosen.length && generation.current === run; index += 3) {
      await Promise.all(chosen.slice(index, index + 3).map(async (cameraId) => {
        try {
          const coverage = await recordingCoverage(cameraId, from, to);
          if (!coverage.supported) throw new Error(t('exports.unsupported'));
          if (coverage.gaps.length) throw new Error(t('exports.gaps'));
          if (run !== generation.current) return;
          const session = await create.mutateAsync({ cameraId, from, to });
          if (run !== generation.current) { await stopCameraPlayback(session.url); return; }
          active.current.push(session.url); setSessions((previous) => ({ ...previous, [cameraId]: session.url }));
        } catch (error) { if (run === generation.current) setErrors((previous) => ({ ...previous, [cameraId]: readActionError(error, t('playback.loadError')) })); }
      }));
    }
    if (run === generation.current) setStarting(false);
  }
  function transport(action: 'play' | 'pause' | 'restart'): void {
    for (const video of grid.current?.querySelectorAll('video') ?? []) {
      if (action === 'play') void video.play().catch(() => undefined);
      else if (action === 'pause') video.pause(); else video.currentTime = 0;
    }
  }
  return <div className="evidence-page">
    <label>{t('exports.site')} <select value={site} onChange={(event) => { setSite(event.target.value); setChosen([]); }}><option value="">{t('exports.chooseSite')}</option>{[...new Set(cameras.map((camera) => camera.siteId))].map((id) => <option key={id} value={id}>{sites.get(id) ?? id}</option>)}</select></label>
    <p>{t('exports.multiLimit', { count: MAX_CAMERAS })}</p>
    <div className="evidence-form">{cameras.filter((camera) => camera.siteId === site && camera.playbackConfigured).map((camera) => <label key={camera.id}><input type="checkbox" checked={chosen.includes(camera.id)} disabled={!chosen.includes(camera.id) && chosen.length >= MAX_CAMERAS} onChange={(event) => setChosen((previous) => event.target.checked ? [...previous, camera.id] : previous.filter((id) => id !== camera.id))} />{camera.name}</label>)}</div>
    <div className="titlebar-actions"><button className="primary-button" disabled={!chosen.length || starting} onClick={() => void start()}>{t('playback.load')}</button><button className="secondary-button" onClick={stop}>{t('exports.stop')}</button>{(['play', 'pause', 'restart'] as const).map((action) => <button key={action} className="secondary-button" disabled={!Object.keys(sessions).length} onClick={() => transport(action)}>{t(`exports.${action}`)}</button>)}</div>
    <div className="playback-grid" ref={grid}>{chosen.map((id) => <article key={id}><h2>{cameras.find((camera) => camera.id === id)?.name}</h2>{sessions[id] && <video key={sessions[id]} src={sessions[id]} controls playsInline onError={() => setErrors((previous) => ({ ...previous, [id]: t('playback.loadError') }))} />}{errors[id] && <p role="alert">{errors[id]}</p>}</article>)}</div>
  </div>;
}
